import { isProxy, isReactive, isRef, toRaw } from 'vue';
import type { ComposerTranslation } from 'vue-i18n';

/**
 * Helper loop-style method to wait for the callback to resolve to a truthy response.
 */
export async function waitForIt(
  callback: (...args: unknown[]) => Promise<boolean>,
  timeout = 100,
  maxTime = 15000,
  ...args: unknown[]
) {
  const startTime = Date.now();
  const poll = async (
    resolve: (value?: unknown) => void,
    reject: (reason?: Error) => void
  ): Promise<void> => {
    if (await callback(...args)) resolve();
    else if (Date.now() - startTime < maxTime) setTimeout(() => poll(resolve, reject), timeout);
    else reject(new Error('Timeout exceeded'));
  };

  return new Promise(poll);
}

/**
 * Helper method to format bytes into human-readable format.
 */
export const formatBytes = (bytes: number, $t: ComposerTranslation, decimals = 2) => {
  // This should be in mebibytes
  if (bytes === 0) {
    return `0 ${$t('common.bytes')}`;
  }

  const k = 1024;
  const dm = Math.floor(decimals <= 0 ? 0 : decimals);
  const sizes = [$t('common.bytes'), 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
};

export const formatToGiga = (bytes: number, decimals = 2) => {
  return (bytes / Math.pow(1024, 3)).toFixed(decimals);
};

export const downloadFile = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 100);
};

export const updateTitle = (translation?: string) => {
  document.title = `${translation ? `${translation} - ` : ''}${import.meta.env.VITE_WEBMAIL_TITLE}`;
};

export function deepToRaw<T>(sourceObj: T): T {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const objectIterator = (input: any): any => {
    if (Array.isArray(input)) {
      return input.map((item) => objectIterator(item));
    }
    if (isRef(input) || isReactive(input) || isProxy(input)) {
      return objectIterator(toRaw(input));
    }
    if (input && typeof input === 'object') {
      return Object.keys(input).reduce((acc, key) => {
        acc[key as keyof typeof acc] = objectIterator(input[key]);
        return acc;
      }, {} as T);
    }
    return input;
  };

  return objectIterator(sourceObj);
}

/**
 * Normalize a boolean-ish env value to a real boolean.
 * Mirrors the API's filter_var(..., FILTER_VALIDATE_BOOLEAN) semantics:
 * "true", "1", "on" and "yes" (any case, surrounding whitespace ignored)
 * are true; everything else - including "false", "0", "no", "off",
 * empty and undefined - is false.
 */
export const envToBoolean = (value: string | number | boolean | undefined): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }

  return ['true', '1', 'on', 'yes'].includes(
    String(value ?? '')
      .trim()
      .toLowerCase()
  );
};
